/**
 * Zones data for the MTG Rules Reference.
 * Defines the game zones where cards can exist, including
 * visibility rules, key interactions, and common transitions.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/zonesData
 */

const ZONE_IDS = {
  LIBRARY: 'library',
  HAND: 'hand',
  BATTLEFIELD: 'battlefield',
  GRAVEYARD: 'graveyard',
  STACK: 'stack',
  EXILE: 'exile',
  COMMAND: 'command',
  ANTE: 'ante',
};

const ZONE_VISIBILITY = {
  HIDDEN: 'Hidden',
  PUBLIC: 'Public',
  PRIVATE: 'Private',
};

const ZONES = [
  {
    id: ZONE_IDS.LIBRARY,
    name: 'Library',
    description:
      'A player\'s library is their deck (the pile of cards they draw from). It is kept face down in a randomized order. Players draw one card from their library at the beginning of each draw step. If a player must draw from an empty library, they lose the game as a state-based action.',
    visibility: ZONE_VISIBILITY.HIDDEN,
    ordered: true,
    keyRules: [
      'Cards in the library are face down. No player may look at or change the order of cards unless an effect allows it.',
      'The library has a defined order. The top card and bottom card are distinct positions.',
      'Effects that "search" the library allow a player to look through it and find a card meeting certain criteria, then shuffle.',
      'If a player would draw a card but their library is empty, they lose the game (SBA 704.5b).',
      'Effects that put cards on top or bottom of the library do not count as drawing.',
    ],
    commonTransitions: [
      'Library → Hand (draw)',
      'Library → Battlefield (effects like "put onto the battlefield")',
      'Library → Graveyard (mill)',
      'Library → Exile (exile from library effects)',
    ],
  },
  {
    id: ZONE_IDS.HAND,
    name: 'Hand',
    description:
      'A player\'s hand contains cards they have drawn but not yet played. Cards in hand are hidden from opponents (unless revealed by an effect). Players normally have a maximum hand size of 7 and must discard down to it during the cleanup step.',
    visibility: ZONE_VISIBILITY.PRIVATE,
    ordered: false,
    keyRules: [
      'Cards in a player\'s hand are visible only to that player unless an effect reveals them.',
      'The default maximum hand size is 7. During the cleanup step, the active player discards down to their maximum hand size.',
      'Some effects change the maximum hand size (e.g., Reliquary Tower removes the limit).',
      'Spells are cast from the hand unless an effect allows casting from another zone.',
      'Lands are played from the hand unless an effect allows playing from another zone.',
    ],
    commonTransitions: [
      'Hand → Stack (casting a spell)',
      'Hand → Battlefield (playing a land)',
      'Hand → Graveyard (discarding)',
      'Hand → Exile (exile from hand effects)',
    ],
  },
  {
    id: ZONE_IDS.BATTLEFIELD,
    name: 'Battlefield',
    description:
      'The battlefield is the shared zone where permanents exist. Creatures, artifacts, enchantments, lands, planeswalkers, and battles exist on the battlefield. Like the stack, exile, and command zones, the battlefield is shared by all players, and all permanents on the battlefield are visible to all players.',
    visibility: ZONE_VISIBILITY.PUBLIC,
    ordered: false,
    keyRules: [
      'Only permanents (creatures, artifacts, enchantments, lands, planeswalkers, battles) exist on the battlefield.',
      'Instants and sorceries never enter the battlefield. They resolve on the stack and go to the graveyard.',
      'The battlefield is a shared zone, but each permanent has a controller.',
      'Permanents enter the battlefield either by resolving from the stack or being put directly onto the battlefield by an effect.',
      'A permanent that leaves the battlefield and returns is a new object with no memory of its previous existence.',
      'Tokens exist only on the battlefield. They cease to exist in any other zone.',
    ],
    commonTransitions: [
      'Battlefield → Graveyard (destroyed, sacrificed, or lethal damage)',
      'Battlefield → Hand (bounced/returned to hand)',
      'Battlefield → Exile (exiled)',
      'Battlefield → Library (tucked)',
      'Battlefield → Command (commander returned to command zone)',
    ],
  },
  {
    id: ZONE_IDS.GRAVEYARD,
    name: 'Graveyard',
    description:
      'A player\'s graveyard is their discard pile. Destroyed creatures, resolved instants and sorceries, discarded cards, and milled cards all go to the graveyard. The graveyard is public, and any player may look through any graveyard at any time.',
    visibility: ZONE_VISIBILITY.PUBLIC,
    ordered: true,
    keyRules: [
      'The graveyard is an ordered zone. Cards stay in the order they were put there. Players may not reorder their graveyard.',
      'Any player may examine any player\'s graveyard at any time.',
      'A creature that is "destroyed" or receives lethal damage is put into its owner\'s graveyard.',
      '"Dies" specifically means a creature moving from the battlefield to the graveyard.',
      'Many effects interact with cards in the graveyard: flashback, unearth, delve, escape, and reanimation spells.',
    ],
    commonTransitions: [
      'Graveyard → Battlefield (reanimation effects like Reanimate)',
      'Graveyard → Hand (recursion effects like Regrowth)',
      'Graveyard → Exile (delve, escape, or exile effects)',
      'Graveyard → Stack (flashback, retrace)',
      'Graveyard → Library (shuffle into library effects)',
    ],
  },
  {
    id: ZONE_IDS.STACK,
    name: 'Stack',
    description:
      'The stack is the zone where spells and abilities wait to resolve. When a player casts a spell or an ability triggers, it is placed on the stack. Objects on the stack resolve one at a time in last-in, first-out (LIFO) order. The stack is a shared, public zone.',
    visibility: ZONE_VISIBILITY.PUBLIC,
    ordered: true,
    keyRules: [
      'Spells and abilities on the stack are visible to all players.',
      'The stack resolves in LIFO order: the last object added resolves first.',
      'An object resolves only when all players pass priority in succession.',
      'Players can respond to objects on the stack by casting instants or activating abilities, adding more objects on top.',
      'Mana abilities and special actions do not use the stack.',
      'A spell on the stack is not yet a permanent. It becomes one only when it resolves (if it is a permanent spell).',
    ],
    commonTransitions: [
      'Stack → Battlefield (permanent spell resolves)',
      'Stack → Graveyard (instant/sorcery resolves, or spell is countered)',
      'Stack → Exile (some spells exile themselves on resolution)',
    ],
  },
  {
    id: ZONE_IDS.EXILE,
    name: 'Exile',
    description:
      'Exile is a zone for cards that have been removed from the game by various effects. Exiled cards are normally face up and public. Exile was formerly called "removed from the game," but exiled cards are still part of the game and can be interacted with by certain effects.',
    visibility: ZONE_VISIBILITY.PUBLIC,
    ordered: false,
    keyRules: [
      'Exiled cards are normally face up unless the effect that exiled them says otherwise.',
      'Face-down exiled cards cannot be examined by any player unless an effect specifically allows it.',
      'Some effects exile cards temporarily ("until [condition]") and return them later.',
      'Cards exiled with specific abilities (like imprint or foretell) can often be cast or used from exile.',
      'Exile is not the same as the graveyard. Effects that interact with the graveyard do not affect exile.',
    ],
    commonTransitions: [
      'Exile → Battlefield (returned from exile by temporary exile effects)',
      'Exile → Stack (cast from exile via cascade, suspend, foretell, etc.)',
      'Exile → Hand (some effects return exiled cards to hand)',
    ],
  },
  {
    id: ZONE_IDS.COMMAND,
    name: 'Command Zone',
    description:
      'The command zone is used for special game objects that affect the game but are not part of the main zones. In Commander, each player\'s commander starts in the command zone. Emblems, dungeon cards, and plane cards also exist in the command zone. The command zone is public.',
    visibility: ZONE_VISIBILITY.PUBLIC,
    ordered: false,
    keyRules: [
      'In Commander, your commander begins the game in the command zone and can be cast from there.',
      'Each time a commander is cast from the command zone, it costs an additional {2} for each previous time it was cast from there (commander tax).',
      'If a commander would go to the graveyard or exile, its owner may choose to put it in the command zone instead.',
      'Emblems are created in the command zone by planeswalker abilities. They cannot be removed by any game effect.',
      'In Oathbreaker, both the Oathbreaker and Signature Spell start in the command zone.',
      'Dungeon cards (from Adventures in the Forgotten Realms) exist in the command zone.',
    ],
    commonTransitions: [
      'Command → Stack (casting commander or signature spell)',
      'Command → Battlefield (commander resolves)',
    ],
  },
  {
    id: ZONE_IDS.ANTE,
    name: 'Ante (Deprecated)',
    description:
      'The ante zone is a legacy zone from Magic\'s earliest rules where players would wager cards. At the start of a game, each player would set aside a random card from their deck as an "ante," and the winner would take all ante cards. Ante is no longer supported in any sanctioned format and all ante cards are banned.',
    visibility: ZONE_VISIBILITY.PUBLIC,
    ordered: false,
    keyRules: [
      'Ante is not used in any current sanctioned format.',
      'All cards with ante mechanics (e.g., Contract from Below, Darkpact, Demonic Attorney) are banned in all formats.',
      'The ante zone was removed from official tournament rules but still exists in the comprehensive rules for completeness.',
    ],
    commonTransitions: [],
  },
];

module.exports = {
  ZONE_IDS,
  ZONE_VISIBILITY,
  ZONES,
};
